import { useCallback, useEffect, useMemo, useState } from "react";
import { listManufacturingDealers } from "@/lib/manufacturing/dealersClient";
import type { MfgSubscriberDealer } from "@/lib/manufacturing/dealerTypes";
import {
  getServiceDefinitionsContext,
  listServiceRigs,
  listServiceTechs,
  patchServiceDefinition,
} from "@/lib/service/client";
import type {
  ServiceDefinitionScope,
  ServiceDefinitionsContext,
  ServiceOffering,
  ServiceRig,
  ServiceTech,
} from "@/lib/service/types";
import { useServiceDefinitionResources } from "./useServiceDefinitionResources";

export type ServiceDefinitionResourcesTab = "rigs" | "techs";

export function useServiceDefinitionsPanel(navTick: number) {
  const [context, setContext] = useState<ServiceDefinitionsContext | null>(null);
  const [rigs, setRigs] = useState<ServiceRig[]>([]);
  const [techs, setTechs] = useState<ServiceTech[]>([]);
  const [subscribers, setSubscribers] = useState<MfgSubscriberDealer[]>([]);
  const [scope, setScopeState] = useState<ServiceDefinitionScope>("general");
  const [resourcesTab, setResourcesTab] = useState<ServiceDefinitionResourcesTab>("rigs");
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [newResourceSignal, setNewResourceSignal] = useState(0);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [quotableDraft, setQuotableDraft] = useState(false);
  const [subscriberDraft, setSubscriberDraft] = useState<string[]>([]);

  const offerings = useMemo(() => context?.offerings ?? [], [context]);

  const activeOffering = useMemo((): ServiceOffering | null => {
    if (scope === "general") return null;
    return offerings.find((o) => o.id === scope) ?? null;
  }, [offerings, scope]);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const [ctxRes, rigsRes, techsRes, dealersRes] = await Promise.all([
      getServiceDefinitionsContext(),
      listServiceRigs(),
      listServiceTechs({ active_only: false }),
      listManufacturingDealers(),
    ]);
    setListBusy(false);
    if (!ctxRes.ok) {
      setLocalError(ctxRes.error);
      return;
    }
    setContext(ctxRes.data);
    if (rigsRes.ok) setRigs(rigsRes.data.rigs);
    if (techsRes.ok) setTechs(techsRes.data.techs);
    if (dealersRes.ok) setSubscribers(dealersRes.data.subscriber_dealers);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  useEffect(() => {
    if (!activeOffering || !context) {
      setDescriptionDraft("");
      setQuotableDraft(false);
      setSubscriberDraft([]);
      return;
    }
    setDescriptionDraft(activeOffering.description);
    setQuotableDraft(activeOffering.quotable_from_sales);
    setSubscriberDraft(context.subscriber_org_ids_by_offering[activeOffering.id] ?? []);
  }, [activeOffering, context]);

  const resources = useServiceDefinitionResources({
    scope,
    rigs,
    techs,
    rigOfferingIds: context?.rig_offering_ids_by_rig ?? {},
    techOfferingIds: context?.tech_offering_ids_by_tech ?? {},
    resourcesTab,
    newResourceSignal,
    onReload: reload,
    onError: setLocalError,
  });

  const setScope = useCallback(
    (next: ServiceDefinitionScope) => {
      resources.trySetScope(next, scope, setScopeState);
    },
    [resources, scope]
  );

  const saveDefinition = useCallback(async () => {
    if (!activeOffering) return;
    setActionBusy(true);
    setLocalError(null);
    const res = await patchServiceDefinition({
      offering_id: activeOffering.id,
      description: descriptionDraft,
      quotable_from_sales: quotableDraft,
      subscriber_org_ids: subscriberDraft,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    if (res.data.product_types_synced) {
      window.dispatchEvent(new CustomEvent("org-settings-saved"));
    }
    await reload();
  }, [activeOffering, descriptionDraft, quotableDraft, reload, subscriberDraft]);

  const toggleSubscriber = useCallback((orgId: string) => {
    setSubscriberDraft((prev) =>
      prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]
    );
  }, []);

  const openOfferingScope = useCallback(
    (offeringId: ServiceDefinitionScope) => {
      resources.trySetScope(offeringId, scope, setScopeState);
    },
    [resources, scope]
  );

  const openGeneralScope = useCallback(() => {
    resources.trySetScope("general", scope, setScopeState);
  }, [resources, scope]);

  return {
    offerings,
    subscribers,
    scope,
    setScope,
    openOfferingScope,
    openGeneralScope,
    activeOffering,
    rigOfferingIds: context?.rig_offering_ids_by_rig ?? {},
    techOfferingIds: context?.tech_offering_ids_by_tech ?? {},
    resourcesTab,
    setResourcesTab,
    listBusy,
    actionBusy: actionBusy || resources.actionBusy,
    localError,
    descriptionDraft,
    setDescriptionDraft,
    quotableDraft,
    setQuotableDraft,
    subscriberDraft,
    toggleSubscriber,
    saveDefinition,
    bumpNewResource: () => setNewResourceSignal((s) => s + 1),
    allRigs: rigs,
    allTechs: techs,
    resources,
  };
}
